/**
 * Referral tracker — persists `?ref=` and `?mro=` URL params across the
 * visitor session so a referee who clicks a shared link can wander the site
 * and still have their referrer credited at registration.
 *
 * Strategy:
 *   - On every route change, read `?ref` / `?mro` from the URL.
 *   - If present, write to BOTH localStorage and a `aga_ref` / `aga_mro`
 *     cookie with a 30-day TTL ("last-touch wins" — newer click overwrites
 *     older).
 *   - `RegisterPage` calls `getStoredRef()` / `getStoredMro()` as a fallback
 *     when no URL param is set on the registration page itself.
 *   - `clearStoredReferral()` is invoked by `AuthContext.login()` after a
 *     successful registration so the cookies don't auto-fill subsequent
 *     unrelated signups on a shared device.
 */
const REF_KEY = 'aga_ref';
const MRO_KEY = 'aga_mro';
const TTL_DAYS = 30;
const TTL_SECONDS = TTL_DAYS * 24 * 60 * 60;

function setCookie(name, value) {
  if (typeof document === 'undefined') return;
  // SameSite=Lax keeps the cookie when arriving from external sites
  // (shared link from chat / email) which is exactly the flow we need.
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${TTL_SECONDS}; path=/; SameSite=Lax`;
}

function getCookie(name) {
  if (typeof document === 'undefined') return '';
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const c of cookies) {
    const idx = c.indexOf('=');
    if (idx === -1) continue;
    if (c.slice(0, idx) === name) {
      try { return decodeURIComponent(c.slice(idx + 1)); } catch { return ''; }
    }
  }
  return '';
}

function deleteCookie(name) {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; max-age=0; path=/; SameSite=Lax`;
}

function lsSet(key, value) {
  try { localStorage.setItem(key, JSON.stringify({ v: value, ts: Date.now() })); } catch { /* localStorage may be unavailable (Safari private mode); cookie still set. */ }
}
function lsGet(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.v || !parsed.ts) return '';
    if (Date.now() - parsed.ts > TTL_SECONDS * 1000) {
      localStorage.removeItem(key);
      return '';
    }
    return parsed.v;
  } catch { return ''; }
}
function lsDel(key) {
  try { localStorage.removeItem(key); } catch { /* see lsSet — non-fatal. */ }
}

/** Capture `?ref` / `?mro` from a URL into both localStorage and a cookie. */
export function captureReferralFromUrl(search) {
  try {
    const params = new URLSearchParams(search || (typeof window !== 'undefined' ? window.location.search : ''));
    const ref = (params.get('ref') || '').trim();
    const mro = (params.get('mro') || '').trim();
    if (ref) {
      lsSet(REF_KEY, ref);
      setCookie(REF_KEY, ref);
    }
    if (mro) {
      lsSet(MRO_KEY, mro);
      setCookie(MRO_KEY, mro);
    }
    return { ref, mro };
  } catch (e) {
    console.debug('referralTracker.capture failed:', e?.message || e);
    return { ref: '', mro: '' };
  }
}

export function getStoredRef() {
  return lsGet(REF_KEY) || getCookie(REF_KEY) || '';
}
export function getStoredMro() {
  return lsGet(MRO_KEY) || getCookie(MRO_KEY) || '';
}

/** Resolve the ref code to use at registration time, preferring an explicit
 *  URL param over the stored value. */
export function resolveRefCode(urlValue) {
  return (urlValue || '').trim() || getStoredRef();
}
export function resolveMroCode(urlValue) {
  return (urlValue || '').trim() || getStoredMro();
}

export function clearStoredReferral() {
  lsDel(REF_KEY);
  lsDel(MRO_KEY);
  deleteCookie(REF_KEY);
  deleteCookie(MRO_KEY);
}
